import FilterForm from 'components/forms/FilterForm';
import dayjs from 'dayjs';
import { CanAccess, HttpError, useCan } from '@refinedev/core';
import { ClassroomTable } from './components/ClassroomTable';
import { CreateClassroomModal } from 'components/modal/classroom/CreateClassroomModal';
import {
  DatePicker,
  Empty,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Switch,
  Table
} from 'antd';
import { EditButton, useSelect, useTable } from '@refinedev/antd';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { IClassroom } from 'interfaces/classrooms';
import { IGraduateDiscipline } from '../../interfaces/graduate_disciplines';
import { ListLayout } from 'components/layout/layout/ListLayout';
import { replaceTimeZone } from '../../services/date';
import { useState } from 'react';

interface IFilter {
  code: string;
  disciplineId: string;
  current: boolean;
  byAutoEnroll: boolean;
  startsAfter: dayjs.Dayjs;
  endsBefore: dayjs.Dayjs;
}

export const ClassroomList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: canCreate } = useCan({
    resource: 'classrooms',
    action: 'create'
  });

  const { tableProps, searchFormProps } = useTable<
    IClassroom,
    HttpError,
    IFilter
  >({
    onSearch: ({
      code,
      disciplineId,
      current,
      byAutoEnroll,
      startsAfter,
      endsBefore
    }) => {
      return [
        {
          field: 'filter[search_code]',
          operator: 'eq',
          value: code
        },
        {
          field: 'filter[by_discipline_id]',
          operator: 'eq',
          value: disciplineId
        },
        {
          field: 'filter[current]',
          operator: 'eq',
          value: current || null
        },
        {
          field: 'filter[by_auto_enroll]',
          operator: 'eq',
          value: byAutoEnroll
        },
        {
          field: 'filter[starts_after]',
          operator: 'eq',
          value: startsAfter && replaceTimeZone(startsAfter)
        },
        {
          field: 'filter[ends_before]',
          operator: 'eq',
          value: endsBefore && replaceTimeZone(endsBefore)
        }
      ];
    },
    sorters: {
      mode: 'server'
    }
  });

  const { selectProps: disciplineSelectProps } = useSelect<IGraduateDiscipline>(
    {
      resource: 'graduate_disciplines',
      optionLabel: 'name',
      optionValue: 'id',

      onSearch: value => [
        {
          field: 'filter[name]',
          operator: 'eq',
          value
        }
      ],

      pagination: {
        mode: 'server',
        pageSize: 100
      }
    }
  );

  return (
    <ListLayout
      title="Turmas"
      createButtonProps={{
        onClick: () => setIsModalOpen(true)
      }}
    >
      <FilterForm searchFormProps={searchFormProps}>
        <Form.Item name="code">
          <Input placeholder="Código da turma" suffix={<SearchOutlined />} />
        </Form.Item>

        <Form.Item name="disciplineId">
          <Select
            allowClear
            {...disciplineSelectProps}
            placeholder="Disciplina"
            notFoundContent={
              <Empty description="Nenhuma disciplina encontrada. Tente refazer sua busca com outros termos" />
            }
          />
        </Form.Item>

        <Form.Item name="startsAfter">
          <DatePicker
            placeholder="Vigência (início)"
            className="w-full"
            format="DD/MM/YYYY"
          />
        </Form.Item>

        <Form.Item name="endsBefore">
          <DatePicker
            placeholder="Vigência (término)"
            className="w-full"
            format="DD/MM/YYYY"
          />
        </Form.Item>

        <Form.Item className=" bg-slate-100 rounded-md p-2">
          <Space align="center" size="large">
            <Space align="center" size="small">
              <span className="text-gray-700">Status:</span>
              <Form.Item name="current" className="mb-0" initialValue={null}>
                <Radio.Group>
                  <Radio.Button value={null}>Todas</Radio.Button>
                  <Radio.Button value={true}>Ativas</Radio.Button>
                  <Radio.Button value={false}>Inativas</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Space>
            <Space align="center" size="small">
              <span className="text-gray-700">Inscrição:</span>
              <Form.Item name="byAutoEnroll" className="mb-0">
                <Switch
                  checkedChildren="Automática"
                  unCheckedChildren="Manual"
                />
              </Form.Item>
            </Space>
          </Space>
        </Form.Item>
      </FilterForm>

      <ClassroomTable tableProps={tableProps}>
        <Table.Column<IClassroom>
          title="Ações"
          dataIndex="actions"
          align="center"
          width={60}
          fixed="right"
          render={(_, record) => {
            return (
              <CanAccess resource="classrooms" action="show">
                <EditButton
                  hideText
                  size="small"
                  recordItemId={record.id}
                  icon={<EyeOutlined />}
                />
              </CanAccess>
            );
          }}
        />
      </ClassroomTable>

      {canCreate && (
        <CreateClassroomModal open={isModalOpen} onClose={setIsModalOpen} />
      )}
    </ListLayout>
  );
};
